"use client";
import { DxfViewer } from "@/components/dxf-viewer";
import { StepViewer } from "@/components/step-viewer";
import { STLViewer } from "@/components/stl-viewer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
// import { Progress } from "@/components/ui/progress"; // Removed unused import
import { ServicesCarousel } from "@/components/services-carousel";
import { FloatingNavigationMenu } from "@/components/ui/floating-nav-menu";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";

const navigationItems = [
  {
    title: "Page 1",
    href: "#",
  },
  {
    title: "Page 2",
    href: "#",
  },
  {
    title: "Page 3",
    href: "#",
  },
];

const sampleFiles = [
  { name: "test-dish.stl", label: "Download Sample STL" },
  { name: "test-manifold.step", label: "Download Sample STEP" },
  { name: "test-guitar.dxf", label: "Download Sample DXF (2D)" },
  { name: "test-bridge.dxf", label: "Download Sample DXF (3D, In Progress)" },
];

export default function Home() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showPrice, setShowPrice] = useState(false);
  const { toast } = useToast();

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      const allowedExtensions = ["stl", "step", "dxf"];
      if (allowedExtensions.includes(fileExtension || "")) {
        if (file.size > 10 * 1024 * 1024) {
          toast({
            title: "File too large",
            description: "Please upload a file smaller than 10MB",
            variant: "destructive",
          });
          event.target.value = "";
          setSelectedFile(null);
          setShowPrice(false);
          return;
        }
        setSelectedFile(file);
        setShowPrice(false);
      } else {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid STL, STEP, or DXF file",
          variant: "destructive",
        });
        event.target.value = "";
        setSelectedFile(null);
        setShowPrice(false);
      }
    } else {
      setSelectedFile(null);
      setShowPrice(false);
    }
  };

  const handleShowQuote = (e: React.MouseEvent) => {
    e.preventDefault();
    if (showPrice) {
      // Navigate to sign up page when "Get Started" is clicked
      window.location.href = "/signup";
      return;
    }
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file first",
        variant: "destructive",
      });
      return;
    }
    setShowPrice(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="py-4">
        <FloatingNavigationMenu items={navigationItems} />
      </header>

      <section className="relative flex flex-col items-center justify-center py-16 md:pb-24 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23000000' fillOpacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
              backgroundSize: "60px 60px",
              maskImage:
                "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
            }}
          ></div>
        </div>
        <div className="container relative z-10 flex flex-col items-center gap-6 px-4">
          <Badge
            variant="secondary"
            className="text-sm font-normal bg-primary/10 text-primary"
          >
            Precision Manufacturing with Protype
          </Badge>
          <h1 className="p-2 text-center text-5xl font-bold tracking-tight sm:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-foreground to-70% to-primary">
            Manufacturing Made Easy
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto text-center px-4">
            Upload your STL or STEP files via Protype and get instant quotes,
            lead times, and previews for your fabricated parts.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 justify-center mt-12 gap-2 px-4 max-w-md md:max-w-2xl lg:max-w-2xl mx-auto">
            {sampleFiles.map((file) => (
              <Button
                key={file.name}
                variant="outline"
                className="flex items-center justify-center gap-2 bg-background/80 backdrop-blur-sm border-primary/20 hover:border-primary/40 transition-colors cursor-pointer shadow-lg"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = `/${file.name}`;
                  link.download = file.name;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-download"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {file.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="w-full max-w-3xl lg:max-w-2xl mt-24">
          <Card className="shadow-xl relative">
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 transition-opacity" />

            <CardHeader className="text-center">
              <Badge variant="outline" className="mb-2 mx-auto">
                Get Started
              </Badge>
              <CardTitle className="text-2xl">Start Your Project</CardTitle>
              <CardDescription>
                Supported formats: STL, STEP, DXF
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6 max-w-2xl mx-auto">
                <div className="grid w-full items-center gap-1.5">
                  <div className="flex flex-col items-center justify-center w-full">
                    <div className="relative w-full">
                      <label
                        htmlFor="file-upload"
                        className="flex flex-col items-center justify-center w-full h-40 md:h-48 lg:h-40 border-2 border-dashed rounded-lg cursor-pointer bg-muted/50 hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            className="w-8 h-8 mb-4 text-muted-foreground"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 16"
                          >
                            <path
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                            />
                          </svg>
                          <p className="mb-2 text-sm text-muted-foreground">
                            <span className="font-semibold">
                              Click to upload
                            </span>{" "}
                            or drag and drop
                          </p>
                          <p className="text-xs text-muted-foreground">
                            STL, STEP or DXF files only (max 10MB)
                          </p>
                        </div>
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        accept=".stl,.step,.dxf"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={handleFileChange}
                      />
                    </div>
                    {selectedFile && (
                      <div className="mt-2 w-full">
                        <p className="text-sm text-muted-foreground">
                          Selected: {selectedFile.name}
                        </p>
                        {selectedFile.name.toLowerCase().endsWith(".stl") && (
                          <div className="mt-4">
                            <STLViewer
                              file={selectedFile}
                              showPrice={showPrice}
                            />
                          </div>
                        )}
                        {selectedFile.name.toLowerCase().endsWith(".step") && (
                          <div className="mt-4">
                            <StepViewer
                              file={selectedFile}
                              showPrice={showPrice}
                            />
                          </div>
                        )}
                        {selectedFile.name.toLowerCase().endsWith(".dxf") && (
                          <div className="mt-4">
                            <DxfViewer
                              file={selectedFile}
                              showPrice={showPrice}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  className="w-full cursor-pointer"
                  onClick={handleShowQuote}
                >
                  {showPrice ? "Get Started" : "Get Quote"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      <section className="py-12 bg-background">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">
            Our Services
          </Badge>
          <h2 className="text-3xl font-bold mb-4">Manufacturing Solutions</h2>
          <Separator className="w-24 mx-auto" />
        </div>
        <ServicesCarousel />
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="bg-primary/95 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            {/* Abstract pattern background */}
            <div className="absolute inset-0 opacity-10">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <defs>
                  <pattern
                    id="grid"
                    width="10"
                    height="10"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="var(--card-foreground)"
                      strokeWidth="0.5"
                    />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>

            <div className="relative z-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-primary-foreground">
                {[
                  { value: "99.8%", label: "Print Success Rate" },
                  { value: "±0.1mm", label: "Precision Accuracy" },
                  { value: "72h", label: "Rapid Turnaround" },
                  { value: "100+", label: "Materials Available" },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold mb-2">
                      {stat.value}
                    </div>
                    <div className="text-primary-foreground/80">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-12 px-6">
        <div>
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              Our Capabilities
            </Badge>
            <h2 className="text-3xl font-bold mb-4">Why Choose Us</h2>
            <Separator className="w-24 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader>
                <CardTitle>Instant Quotes</CardTitle>
                <CardDescription>
                  Get accurate pricing in seconds
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>
                  Our advanced algorithm calculates the exact cost based on
                  material, size, and complexity.
                </p>
              </CardContent>
            </Card>
            <Card className="relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader>
                <CardTitle>Real-time Preview</CardTitle>
                <CardDescription>
                  See your model before printing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>
                  Interactive 3D preview with material options and print
                  orientation suggestions.
                </p>
              </CardContent>
            </Card>
            <Card className="relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardHeader>
                <CardTitle>Fast Turnaround</CardTitle>
                <CardDescription>Quick delivery guaranteed</CardDescription>
              </CardHeader>
              <CardContent>
                <p>
                  Get your parts delivered within days, not weeks, with our
                  efficient production process.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
